import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { toTimeDDMonYearFormat, toReadableTimeFromNow, round } from 'utils';
import { Schedule } from 'models/schedules';

interface Props {
    schedule: Schedule;
}

const TLEDetails: React.FunctionComponent<Props> = ({ schedule }) => {
    const theme = useTheme();

    const { orbit, pass_details: details } = schedule;

    const { satellite, tle } = orbit ?? {};

    return (
        <Paper
            sx={{
                display: 'grid',
                gridTemplateRows: 'auto auto auto 1fr auto',
                background: theme.palette.background.details,
                px: 1,
                borderRadius: 1,
                rowGap: 1,
                breakInside: 'avoid',
            }}
        >
            <Typography variant="h6">TLE Details</Typography>
            <Box
                sx={{
                    display: 'grid',
                    rowGap: 1,
                    columnGap: 2,
                    gridTemplateColumns: 'auto 1fr',
                    [theme.breakpoints.up('xl')]: {
                        gridTemplateColumns: 'auto 1fr auto 1fr',
                    },
                    width: '100%',
                }}
            >
                <Box sx={{ fontWeight: 'bold' }}>Satellite Name:</Box>
                <Box>{satellite?.name ?? schedule.satelliteName}</Box>
                <Box sx={{ fontWeight: 'bold' }}>Catalogue Number:</Box>
                <Box>
                    {satellite?.catalogue_number ??
                        details?.tle_line2.substring(2, 7) ??
                        ''}
                </Box>

                {tle && (
                    <>
                        <Box
                            sx={{
                                fontWeight: 'bold',
                                gridColumn: 'span 1',
                            }}
                        >
                            Epoch:
                        </Box>
                        <Box sx={{ gridColumn: 'span 3' }}>
                            {toTimeDDMonYearFormat(tle.epoch)} (
                            {toReadableTimeFromNow(tle.epoch)})
                        </Box>
                    </>
                )}
                {!tle && details && (
                    <>
                        <Box
                            sx={{
                                fontWeight: 'bold',
                                gridColumn: 'span 1',
                            }}
                        >
                            Epoch:
                        </Box>
                        <Box sx={{ gridColumn: 'span 3' }}>
                            {toTimeDDMonYearFormat(details.tle_epoch)} (
                            {toReadableTimeFromNow(details.tle_epoch)})
                        </Box>
                    </>
                )}
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    rowGap: 1,
                    width: '100%',
                }}
            >
                <Box sx={{ fontWeight: 'bold' }}>TLE:</Box>
                <Box>{tle?.line0 ?? details?.tle_line0}</Box>
                <Box>{tle?.line1 ?? details?.tle_line1}</Box>
                <Box>{tle?.line2 ?? details?.tle_line2}</Box>
            </Box>

            {/* Space Track Extra Fields */}
            {tle && (
                <Box
                    sx={{
                        display: 'grid',
                        rowGap: 1,
                        columnGap: 2,
                        gridTemplateColumns: 'auto 1fr',
                        [theme.breakpoints.up('xl')]: {
                            gridTemplateColumns: 'auto 1fr auto 1fr',
                        },
                        width: '100%',
                    }}
                >
                    <Box sx={{ fontWeight: 'bold' }}>Apogee:</Box>
                    <Box>{tle.apogee} Km</Box>
                    <Box sx={{ fontWeight: 'bold' }}>Perigee:</Box>
                    <Box>{tle.perigee} Km</Box>

                    <Box sx={{ fontWeight: 'bold' }}>Period:</Box>
                    <Box>
                        {round(tle.period)}m {round((tle.period % 1) * 60)}s
                    </Box>
                    <Box sx={{ fontWeight: 'bold' }}>Eccentricity:</Box>
                    <Box>{tle.eccentricity}</Box>

                    <Box sx={{ fontWeight: 'bold' }}>Inclination:</Box>
                    <Box>{tle.inclination}°</Box>
                    <Box sx={{ fontWeight: 'bold' }}>RAAN:</Box>
                    <Box>{tle.RAAN}°</Box>
                </Box>
            )}
        </Paper>
    );
};

TLEDetails.displayName = 'TLEDetails';
export default TLEDetails;
